import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Phone, ShieldCheck, Star, Home as HomeIcon } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";
import PropertyCard from "@/components/PropertyCard";
import NotFound from "@/pages/NotFound";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { trackEvent } from "@/lib/gtag";
import { LANDING_PAGES, TRUST_POINTS } from "@/lib/landingPages";

const SUPPORT_PHONE = "+919000000000";

export default function Landing() {
  const { slug } = useParams();
  const page = LANDING_PAGES[slug];
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!page) return;
    window.scrollTo(0, 0);
    api
      .get("/properties", { params: { ...(page.city ? { city: page.city } : {}), limit: 4 } })
      .then((r) => setItems(r.data.items))
      .catch(() => setItems([]));
  }, [page]);

  useSeo({
    title: page ? page.headline.slice(0, 60) : "Page not found",
    description: page?.sub,
    path: page ? `/lp/${slug}` : undefined,
    noindex: true, // ad landing pages shouldn't compete with the SEO city pages
  });

  if (!page) return <NotFound />;

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* minimal header — no nav, nothing to click away with */}
      <header className="border-b border-border">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 h-[64px] flex items-center justify-between">
          <span className="flex items-center gap-2 text-primary">
            <HomeIcon className="h-6 w-6" strokeWidth={2.2} />
            <span className="font-display text-[17px] tracking-tight">HOUSES7.COM</span>
          </span>
          <a
            href={`tel:${SUPPORT_PHONE}`}
            onClick={() => trackEvent("call_owner", { event_category: "contact", source: `lp:${slug}` })}
            className="flex items-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-[13px] font-bold text-background hover:brightness-110 transition-all duration-200"
            data-testid="landing-call-btn"
          >
            <Phone className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Call us</span>
          </a>
        </div>
      </header>

      <section className="relative">
        <img src={page.hero} alt={page.headline} className="h-[420px] md:h-[520px] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/25" />
        <div className="absolute inset-0">
          <div className="mx-auto grid h-full max-w-[1200px] items-center gap-10 px-5 md:px-10 lg:grid-cols-[1fr_380px]">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/85 fade-up">{page.eyebrow}</p>
              <h1 className="mt-3 max-w-xl font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.08] fade-up" style={{ animationDelay: "0.05s" }}>
                {page.headline}
              </h1>
              <p className="mt-4 max-w-lg text-[13px] md:text-base text-white/85 fade-up" style={{ animationDelay: "0.1s" }}>
                {page.sub}
              </p>
              <div className="mt-6 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "0.15s" }}>
                <Link
                  to="/signup"
                  onClick={() => trackEvent("lp_signup_click", { source: `lp:${slug}` })}
                  className="rounded-lg bg-primary px-6 py-3.5 text-[14px] font-bold text-primary-foreground hover:brightness-95 transition-all duration-200"
                  data-testid="landing-signup-btn"
                >
                  {page.ownerFirst ? "List my property free" : "Create free account"}
                </Link>
                <Link
                  to={page.browsePath}
                  className="rounded-lg border border-white/40 px-6 py-3.5 text-[14px] font-bold text-white hover:bg-white/10 transition-colors duration-200"
                  data-testid="landing-browse-btn"
                >
                  {page.ownerFirst ? "See how it works" : "Browse listings"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1200px] px-5 md:px-10 -mt-16 relative z-10 lg:-mt-24">
        <div className="ml-auto max-w-md rounded-2xl border border-border bg-background p-6 shadow-panel" data-testid="landing-enquiry-card">
          <p className="font-display text-xl">
            {page.ownerFirst ? "Want us to list it for you?" : `Tell us what you're looking for${page.city ? ` in ${page.city}` : ""}`}
          </p>
          <p className="mt-1.5 text-[13px] text-muted-foreground">
            Share your number and we'll call you back with matching options. No brokerage, no spam.
          </p>
          <div className="mt-5">
            <EnquiryForm compact source={`lp:${slug}`} submitLabel={page.ownerFirst ? "Request a callback" : "Get matching options"} />
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-[1200px] px-5 md:px-10 mt-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_POINTS.map((t) => (
            <div key={t.title} className="rounded-2xl border border-border p-5">
              <span className="inline-flex rounded-xl bg-primary/10 p-2.5 text-primary">
                <Check className="h-4 w-4" strokeWidth={3} />
              </span>
              <p className="mt-3 font-display text-[15px]">{t.title}</p>
              <p className="mt-1 text-[13px] text-muted-foreground">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {items.length > 0 && (
        <section className="mx-auto max-w-[1200px] px-5 md:px-10 mt-16">
          <h2 className="font-display text-lg">
            Recently listed{page.city ? ` in ${page.city}` : ""}
          </h2>
          <div className="mt-6 grid gap-x-3.5 gap-y-6 sm:gap-x-6 grid-cols-2 lg:grid-cols-4" data-testid="landing-listings">
            {items.map((p) => (
              <PropertyCard key={p.property_id} prop={p} />
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1200px] px-5 md:px-10 mt-16">
        <div className="rounded-2xl bg-foreground p-8 md:p-12 text-background">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-background/70">
            <ShieldCheck className="h-3.5 w-3.5" /> Trusted by owners and tenants
          </p>
          <p className="mt-3 max-w-2xl font-display text-2xl md:text-3xl leading-snug">
            {page.ownerFirst
              ? "Post once, get enquiries directly on your phone."
              : "Skip the brokers. Talk to the person who actually owns the place."}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/signup"
              onClick={() => trackEvent("lp_signup_click", { source: `lp:${slug}`, position: "footer" })}
              className="rounded-lg bg-primary px-6 py-3.5 text-[14px] font-bold text-primary-foreground hover:brightness-95 transition-all duration-200"
              data-testid="landing-signup-btn-footer"
            >
              {page.ownerFirst ? "List my property free" : "Get started free"}
            </Link>
            <a
              href={`tel:${SUPPORT_PHONE}`}
              onClick={() => trackEvent("call_owner", { event_category: "contact", source: `lp:${slug}`, position: "footer" })}
              className="flex items-center gap-2 rounded-lg border border-background/30 px-6 py-3.5 text-[14px] font-bold hover:bg-background/10 transition-colors duration-200"
            >
              <Phone className="h-4 w-4" /> Talk to us
            </a>
          </div>
          <p className="mt-6 flex items-center gap-1.5 text-[12px] text-background/70">
            <Star className="h-3.5 w-3.5 fill-background/70" /> Verified listings across Pune, Mumbai, Delhi NCR, Bengaluru and more
          </p>
        </div>
      </section>

      <footer className="mx-auto max-w-[1200px] px-5 md:px-10 mt-14 pb-12 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted-foreground">
        <span>© {new Date().getFullYear()} Houses7.com</span>
        <Link to="/privacy" className="hover:text-foreground transition-colors duration-200">Privacy</Link>
        <Link to="/terms" className="hover:text-foreground transition-colors duration-200">Terms</Link>
        <Link to="/" className="hover:text-foreground transition-colors duration-200">Full site</Link>
      </footer>
    </div>
  );
}
